import { FlightlineEvent } from "../models/FlightlineEvent"
import {
    EventReviewGetDTO,
    EventReviewPutDTO,
    FlightlineEventReview,
    GeometryQCStatus,
    MissionReviewPutDTO,
    MissionReviewType,
    ReviewDistanceInfo,
    ReviewNode,
} from "../models/MissionReview"
import { CreatedFlightPlannedLine } from "../store/types/FlightPlansTypes"
import { unplannedLineLabel, unplannedLineValue } from "./FlightlineEventUtils"

export enum ReviewType {
    NODE = "NODE",
    DISTANCE = "DISTANCE",
}

export const reviewDrawerMinWidth = 350
export const reviewTableLabel = "Flightline Event Review"

export class MissionReviewUtils {
    static checkValidMissionReview = (
        missionReview?: MissionReviewType | MissionReviewPutDTO,
    ): missionReview is MissionReviewPutDTO => {
        if (!missionReview) {
            return false
        }
        const { reviewer_id, review_date } = missionReview
        return !!(reviewer_id && review_date)
    }

    static checkValidEventReviews = (reviews?: FlightlineEventReview[]): reviews is FlightlineEventReview[] => {
        return reviews?.length !== 0
    }

    static transformEventToReview = (
        event: FlightlineEvent,
        id: number,
        eventPlannedLines?: CreatedFlightPlannedLine[],
        review?: EventReviewGetDTO,
        from_split_event = false,
    ): FlightlineEventReview => {
        let firstNode: ReviewNode | null = null
        let lastNode: ReviewNode | null = null
        const node_events: ReviewNode[] = event.node_events ?? []

        if (review && node_events?.length) {
            firstNode = node_events.find((node) => node.id === review.start_node_id) ?? null
            lastNode = node_events.find((node) => node.id === review.stop_node_id) ?? null
        } else if (node_events?.length) {
            firstNode = node_events[0]
            lastNode = node_events[node_events.length - 1]
        }

        const fe_is_unplanned_line = event.fe_planned_line_ids.includes(unplannedLineValue)

        let nodeCount = 0
        if (firstNode && lastNode) {
            nodeCount = (lastNode.node_original_name ?? 0) - (firstNode?.node_original_name ?? 0) + 1
        }

        let nodeInfo
        if (firstNode && lastNode) {
            nodeInfo = {
                node_events,
                start_node_name: firstNode.node_original_name,
                start_node_id: firstNode.id,
                stop_node_name: lastNode.node_original_name,
                stop_node_id: lastNode.id,
                node_count: nodeCount,
            }
        }

        let distanceInfo: ReviewDistanceInfo | undefined
        if (!nodeCount && event.event_distance_nmi != null) {
            let startDist = 0.0
            let stopDist
            if (review?.start_distance_nm != null || review?.stop_distance_nm != null) {
                startDist = review.start_distance_nm ?? 0.0
                stopDist = review.stop_distance_nm ?? event.event_distance_nmi
                if (stopDist == null) {
                    throw new Error(
                        `We must have one of "stop_distance_nm" or "event_distance_nmi" to generate a correct event review.`,
                    )
                }
            } else {
                stopDist = event.event_distance_nmi
            }

            distanceInfo = getDistanceInfo(startDist, stopDist)
        }

        let flightPlan = ""
        const plannedLineLabels: string[] = []
        eventPlannedLines?.forEach((pl) => {
            if (pl) {
                plannedLineLabels.push(`${pl.flight_line_name}`)
                if (!flightPlan) {
                    flightPlan = String(pl.flight_plan_id)
                }
            }
        })
        const longLabel = fe_is_unplanned_line ? unplannedLineLabel : `${flightPlan} - ${plannedLineLabels.join(", ")}`
        let shortLabel = longLabel
        if (plannedLineLabels.length > 3) {
            shortLabel = `${flightPlan} - ${plannedLineLabels.slice(0, 3).join(", ")}...`
        }

        return {
            from_split_event,
            fe_review_id: id,
            id: review?.id,
            qc_status: review?.qc_status,
            qc_comment: review?.qc_comment,
            flightplan_name_long_label: longLabel,
            flightplan_name_short_label: shortLabel,
            event,
            node_info: nodeInfo,
            distance_info: distanceInfo,
            angle_degrees: event?.angle_degrees,
            shape_geojson: review?.shape_geojson ?? event.geojson,
        }
    }

    static transformEventReviewToReviewDTO = (reviews: FlightlineEventReview[]): EventReviewPutDTO[] => {
        return reviews.map((review): EventReviewPutDTO => {
            /**
             * We only need to send the start/stop distance data if the line has been split or edited
             */
            const canSendDistanceInfo = review.from_split_event
            const sendStart = canSendDistanceInfo && review.segment_number !== 1
            const sendStop = canSendDistanceInfo && !review.is_final_segment
            return {
                id: review.id,
                flight_line_event_id: review.event.flight_line_event_id,
                qc_status: review.qc_status,
                qc_comment: review.qc_comment,
                start_node_id: review.node_info?.start_node_id,
                stop_node_id: review.node_info?.stop_node_id,
                start_distance_nm: sendStart ? review.distance_info?.start_distance_nm : undefined,
                stop_distance_nm: sendStop ? review.distance_info?.stop_distance_nm : undefined,
            }
        })
    }
}

export const getDistanceInfo = (start: number, stop: number): ReviewDistanceInfo => {
    return {
        start_distance_nm: +start.toFixed(2),
        stop_distance_nm: +stop.toFixed(2),
        length: +(stop - start).toFixed(2),
    }
}

export const getQCStatusForEvent = (isNewEvent: boolean, qcStatus?: GeometryQCStatus) => {
    const originalEventQCStatus = qcStatus ?? GeometryQCStatus.ACCEPTED
    if (!isNewEvent) {
        // This is the original segment, retain original status (with default to Accepted)
        return originalEventQCStatus
    }

    // If rejected is the original status, default all others to Accepted. Otherwise, set them to Rejected.
    if (originalEventQCStatus === GeometryQCStatus.REJECTED) {
        return GeometryQCStatus.ACCEPTED
    }
    return GeometryQCStatus.REJECTED
}

export const canSplitReview = (review: FlightlineEventReview) => {
    const isUnplannedLine = review.event.fe_planned_line_ids.includes(unplannedLineValue)
    const isMultiPlannedLine = review.event.fe_planned_line_ids.length > 1
    return !isUnplannedLine && !isMultiPlannedLine
}
